import { isLoading, Loading } from "../loading";
import { useAllTaskTypes } from "./useTaskTypes";
import { Task } from "../types/Task";
import { useRedirect } from "./useRedirect";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateTestbuyQueries } from "@coworker/apprestructured/src/testbuy/services/queryKeyHelper";
import useSearchParamState from "../../shared/hooks/useSearchParamState";

export const useAfterCreate = (
  task: Record<string, unknown> | null | undefined | Loading
): ((createdTask: Task) => Promise<void>) => {
  const { data: taskTypes, isLoading: isTaskTypesLoading } = useAllTaskTypes();
  const redirect = useRedirect();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParamState();

  if (
    isLoading(task) ||
    isTaskTypesLoading ||
    !taskTypes ||
    task === undefined
  ) {
    return () => Promise.resolve();
  }

  const executeAfterCreate = async (createdTask: Task) => {
    const createdTaskType = taskTypes.find(
      (type) => type.id === createdTask.task_type_id
    );

    const afterCreate = createdTaskType?.after_create;

    if (!afterCreate) {
      return;
    }

    const { id, props } = afterCreate;

    switch (id) {
      case "redirect": {
        redirect(props.url, props.openNewTab, createdTask);
        return;
      }
      case "link-testbuy-article": {
        // Linking the follow-up task to the article (and syncing the testbuy's
        // progress) now happens server-side (tasks-service runAfterCreate).
        // We just invalidate the cache so the UI re-fetches the updated testbuy.
        const { testbuy_id } = createdTask.details as {
          testbuy_id: string;
        };

        invalidateTestbuyQueries(queryClient, testbuy_id);

        // Close the task viewer/creator sheets and go back to the testbuy
        // overview, keeping the sheet-based flow instead of navigating away
        // to the legacy full-page testbuy view.
        setSearchParams((params) => {
          params.delete("taskViewer");
          params.delete("taskCreator");
          params.delete("initialValues");
          params.delete("testbuyArticleId");
          // Deliberately bypass the generic returnTo restore mechanism: after creating
          // a follow-up task we always go to the testbuy overview, not back to the article.
          params.delete("returnTo");
          params.set("testbuyId", testbuy_id);
        });

        return;
      }
      default:
        break;
    }
  };

  return executeAfterCreate;
};
